<template>
    <div class="request-store request-store_loader">
        <template v-if="!isLoading">
            <ul class="request-store__list">
                <template v-for="request in displayRequests">
                    <li :key="request.id" class="request-store__item">
                        <slot :request="request">{{ request.id }}</slot>
                    </li>
                </template>
            </ul>
        </template>

        <div class="request-store__loading" v-else>
            <VSpinner></VSpinner>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import http from '../../../js/http';
import axios from 'axios';
import Component from 'vue-class-component';
import { Prop, Emit, Watch } from 'vue-property-decorator';

import oidc from '@/vue-oidc-client';
import VSpinner from '@/views/components/VProgress/VSpinner';

// Счётчик и пейджер живут в RequestStoreFooter: он рисуется вне области прокрутки,
// поэтому не уезжает вместе с таблицей. Отсюда наружу они уходят событием loaded.
//
// Фоновое обновление списка привязано к выдаче нового токена (userLoaded у oidc-client).
// До 8f013ca7 протухание токена вызывало полный signinRedirect — вкладка перезагружалась
// и список приходил свежим, это и был единственный способ увидеть новые заявки без F5.
// Тихое продление убрало перезагрузку, а вместе с ней и обновление списка; здесь тот же
// триггер возвращён, но вместо перезагрузки страницы перезапрашивается только список.
@Component({
    components: {
        VSpinner,
    },
})
class RequestStorePagination extends Vue {
    @Prop({ type: Number, default: 1 }) value;
    @Prop({ type: Number, default: 15 }) perPage;
    @Prop({ type: Function, default: null }) filterFn;
    @Prop({ type: String, default: '' }) endpoint;
    @Prop({ type: Object, default: null }) query;

    requests = [];
    total = 0;
    totalPages = 1;
    currentPage = 1;
    isLoading = false;
    abortController = null;
    requestSeq = 0;
    isRefreshing = false;

    get displayRequests() {
        return this.filterFn ? this.requests.filter(this.filterFn) : this.requests;
    }

    @Emit('loaded')
    @Emit('request-store:loaded')
    loaded() {
        return this.getEventData();
    }

    handler(page) {
        this.isLoading = true;
        if (!this.dispatchRequest(page)) {
            this.isLoading = false;
        }
    }

    dispatchRequest(page) {
        if (!this.query) return false;

        this.isLoading = true;

        // отменяем предыдущий незавершённый запрос
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        const seq = ++this.requestSeq;
        const data = {
            page,
            perPage: this.perPage,
            ...this.query,
        };

        http.post(this.endpoint, data, { signal: this.abortController.signal })
            .then(({ data: response }) => {
                if (seq !== this.requestSeq) return;
                this.total = response.total;
                this.totalPages = response.last_page;
                this.currentPage = response.current_page;

                this.$emit('input', this.currentPage);

                this.requests = response.data;

                this.isLoading = false;

                this.loaded();
            })
            .catch((error) => {
                // отменённый запрос — не ошибка
                if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return;
                console.warn(error);
                this.isLoading = false;
            });

        return true;
    }

    // Тихое обновление текущей страницы: без спиннера (список не мигает и не теряет прокрутку),
    // с той же страницей, perPage и query — фильтры/поиск/сортировка/пагинация не сбрасываются.
    // Возвращает false, если запуск сейчас не нужен: нет query или уже идёт загрузка
    // (пользовательская или предыдущий refresh) — параллельных запросов не плодим.
    refresh() {
        if (!this.query || this.isLoading || this.isRefreshing || this._isDestroyed) return false;

        this.isRefreshing = true;
        this.abortController = new AbortController();
        const seq = ++this.requestSeq;
        const page = this.currentPage;
        const data = {
            page,
            perPage: this.perPage,
            ...this.query,
        };

        http.post(this.endpoint, data, { signal: this.abortController.signal })
            .then(({ data: response }) => {
                // Пока ждали ответ, пользователь сменил фильтр/страницу — ответ устарел.
                if (seq !== this.requestSeq) return;

                // Заявок стало меньше и текущей страницы больше нет: уходим на последнюю
                // существующую обычной загрузкой, а не показываем пустой список.
                const lastPage = Math.max(1, response.last_page || 1);
                if (page > lastPage) {
                    this.isRefreshing = false;
                    this.handler(lastPage);
                    return;
                }

                this.total = response.total;
                this.totalPages = response.last_page;
                this.requests = response.data;

                this.loaded();
                this.$emit('refreshed', this.getEventData());
            })
            .catch((error) => {
                if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return;
                // Фоновая ошибка не должна ломать уже показанный список — только лог.
                console.warn(error);
            })
            .finally(() => {
                // refresh в каждый момент максимум один (см. guard выше), поэтому флаг
                // снимаем безусловно — в том числе когда его отменил пользовательский запрос.
                this.isRefreshing = false;
            });

        return true;
    }

    // Новый токен выдан (тихое продление сессии) — момент, когда раньше перезагружалась вкладка.
    onSessionRenewed() {
        this.refresh();
    }

    getEndpoint(page) {
        const endpoint = new URL(this.endpoint, window.origin);
        endpoint.searchParams.set('page', page);
        endpoint.searchParams.set('per_page', this.perPage);
        return endpoint;
    }

    getEventData() {
        return {
            requests: this.requests,
            total: this.total,
            totalPages: this.totalPages,
            currentPage: this.currentPage,
        };
    }

    @Watch('value')
    onValueChange(page) {
        // Страницу переключает футер снаружи компонента, поэтому v-model должен работать
        // в обе стороны: раньше value читался только в created. Сравнение с currentPage
        // гасит обратную волну — после загрузки мы сами эмитим input с этой же страницей.
        if (page === this.currentPage) return;
        this.handler(page);
    }

    @Watch('query', { immediate: true })
    onQueryChange(query) {
        if (!query) return;
        this.handler(1);
    }

    @Watch('perPage')
    onPerPageChange() {
        // perPage — обычный проп, сам по себе не триггерит перезагрузку (в отличие от query),
        // поэтому дёргаем её явно и сбрасываем на первую страницу.
        if (!this.query) return;
        this.handler(1);
    }

    @Watch('$route.params.method')
    onRouteMethodChange() {
        // Компонент общий на все разделы (inbox/outbox/store/...) и не перемонтируется при
        // смене раздела — без явной очистки список предыдущего раздела остаётся отрендерен,
        // пока не придёт ответ нового endpoint.
        this.requests = [];
    }

    created() {
        this.currentPage = this.value;
    }

    mounted() {
        oidc.events.addUserLoaded(this.onSessionRenewed);
    }

    beforeDestroy() {
        oidc.events.removeUserLoaded(this.onSessionRenewed);
        if (this.abortController) {
            this.abortController.abort();
        }
    }
}

export default RequestStorePagination;
</script>
<style lang="scss">
.request-store {
    // Сброс браузерных умолчаний ul: раньше это правило приходило из
    // RequestStoreLoader, который больше не используется, и список получал
    // лишние 14px сверху и снизу.
    &__list {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__item {
        width: 100%;
    }

    &__loading {
        display: flex;
        width: 100%;
        height: 200px;
        align-items: center;
        justify-content: center;
    }
}
</style>
